'use client';

import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { Button } from '@savvy-kit/components/button';
import Banner from '@savvy-kit/patterns/banner';
import { graphql } from 'src/gql';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { removeLoggedInAsAdminID } from '@savvy/deprecated-shared/utils/savvy-auth/login-as-advisor';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

const ADVISOR_QUERY = graphql(/* GraphQL */ `
  query AdvisorFromAdminImpersonationBar($id: ID!) {
    advisor(id: $id) {
      id
      user {
        id
        firstName
        lastName
        email
        role
      }
    }
  }
`);

export const STOP_IMPERSONATING_ADVISOR = graphql(/* GraphQL */ `
  mutation StopImpersonatingAdvisor($input: StopImpersonatingUserInput!) {
    stopImpersonatingUser(input: $input) {
      trueUser {
        id
      }
      currentUser {
        id
      }
    }
  }
`);

function AdminImpersonationBarV2() {
  const { user } = useAuth();
  const { data } = useQueryWithErrorLogging(ADVISOR_QUERY, {
    variables: {
      id: user?.id ?? '',
    },
  });

  const [stopImpersonatingUser] = useMutationWithErrorLogging(STOP_IMPERSONATING_ADVISOR);
  const apolloClient: ApolloClient<object> = useApolloClient();

  if (!data?.advisor) return null;

  const handleLogoutFromAdvisor = async () => {
    await stopImpersonatingUser({
      variables: {
        input: {},
      },
    });

    removeLoggedInAsAdminID();
    apolloClient.clearStore();
    window.location.href = '/internal/admin/login-as-advisor';
  };

  const advisor = data.advisor;
  const advisorName = `${advisor.user.firstName} ${advisor.user.lastName}`;

  return (
    <Banner
      variant="info"
      className="rounded-none fixed top-0 z-49"
      description={`You are currently logged in as ${advisorName}`}
      actionButton={
        <Button size="sm" onClick={handleLogoutFromAdvisor}>{`Logout of ${advisor.user.firstName}`}</Button>
      }
    />
  );
}

export default AdminImpersonationBarV2;
